import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Sidenav from "./widgets/Sidenav";

import theme from "./assets/theme";

import themeDark from "./assets/theme-dark";

import routes from "./routes";

import { useMaterialUIController, setMiniSidenav } from "./context";
import { ConfirmProvider } from "./components/feedback";
import { setGlobalErrorHandler } from "./api/api";
import ErrorModal from "./components/Error";
import Notification from "./components/Notification";
import "./App.css";
import ProtectedRoute from "./services/ProtectedRoute";
import Configurator from "./widgets/Configurator";
import { canAccessRouteEntry } from "./utils/authorization";
import { isPublicAppPath, refreshAuthFromSession } from "./utils/authBootstrap";
import FullscreenLoadingOverlay from "./components/FullscreenLoadingOverlay";
import { DisplayLocalizationProvider } from "./lib/datetime/DisplayLocalizationProvider";
import { useSyncUiLanguageFromCustomerLocale } from "./i18n/useUiLanguage";

import logoDarkTheme from "./assets/images/logo_dark.png";
import logoLightTheme from "./assets/images/logo_light.png";

function UiLanguageSync() {
  useSyncUiLanguageFromCustomerLocale();
  return null;
}

export default function App() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [securityChecking, setSecurityChecking] = useState(() => !isPublicAppPath(window.location.pathname));
  const [authReady, setAuthReady] = useState(() => isPublicAppPath(window.location.pathname));

  const brandName = "INCIDENT MANAGEMENT";
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    sidenavPinned,
    direction,
    layout,
    sidenavColor,
    darkMode,
  } = controller;
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [profilesMenuVisible, setProfilesMenuVisible] = useState(false);

  const [isModalOpen, setModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const notificationElement = useRef<any>(null);
  /** True only while the drawer was widened by hover on the icon rail (so leave can collapse). */
  const sidenavHoverRailExpandRef = useRef(false);
  const sidenavHoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sidenavPinnedRef = useRef(sidenavPinned);
  sidenavPinnedRef.current = sidenavPinned;
  const sessionBootstrappedRef = useRef(false);

  const showSnack = (opts: any) => {
    if (notificationElement.current) {
      notificationElement.current.show(opts);
    }
  };

  // Window event listener for global notification system
  useEffect(() => {
    const handleGlobalNotification = (event: any) => {
      showSnack(event.detail);
    };

    window.addEventListener("showGlobalNotification", handleGlobalNotification);

    return () => {
      window.removeEventListener(
        "showGlobalNotification",
        handleGlobalNotification
      );
    };
  }, []);

  useEffect(() => {
    const handleLoading = (event: any) => {
      setLoading(event.detail);
    };

    window.addEventListener("loading", handleLoading);

    setGlobalErrorHandler((e: any) => {
      const fallbackContent =
        e?.raw?.message ||
        e?.raw?.error ||
        e?.response?.data?.message ||
        e?.response?.message ||
        (typeof e === "string" ? e : null) ||
        "An unexpected error occurred.";

      const opts: any = {
        color: "error",
        icon: "error",
        title: e?.title || "Unexpected Error",
        reason: e?.reason || "API Request",
        content: e?.content || fallbackContent,
        info: e?.info || "UNKNOWN_ERROR",
      };

      showSnack(opts);
    });

    let cancelled = false;

    const runSecurityBootstrap = async () => {
      const isPublicRoute = isPublicAppPath(pathname);
      if (isPublicRoute) {
        if (!cancelled) {
          setAuthReady(true);
          setSecurityChecking(false);
        }
        return;
      }

      const shouldBlockUi = !sessionBootstrappedRef.current;
      if (!cancelled && shouldBlockUi) {
        setSecurityChecking(true);
        setAuthReady(false);
      }

      let sessionValid = false;

      try {
        const bootstrapResult = await refreshAuthFromSession();
        sessionValid = bootstrapResult.sessionValid;

        if (!cancelled && sessionValid && bootstrapResult.user) {
          setUser(bootstrapResult.user);
          setUserRoles(bootstrapResult.roles);
          setProfilesMenuVisible(bootstrapResult.profilesMenuVisible);
          sessionBootstrappedRef.current = true;
          return;
        }

        if (!cancelled) {
          setUser(null);
          setUserRoles([]);
          setProfilesMenuVisible(false);
          sessionBootstrappedRef.current = false;
          navigate("/login", { replace: true });
        }
      } catch (err) {
        console.error("Failed to validate authenticated session:", err);
        if (!cancelled) {
          setUser(null);
          setUserRoles([]);
          setProfilesMenuVisible(false);
          sessionBootstrappedRef.current = false;
          navigate("/login", { replace: true });
        }
      } finally {
        if (!cancelled) {
          setSecurityChecking(false);
          if (isPublicRoute || sessionValid) {
            setAuthReady(true);
          }
        }
      }
    };

    runSecurityBootstrap();

    return () => {
      cancelled = true;
      window.removeEventListener("loading", handleLoading);
    };
  }, [pathname, navigate]);

  // Modal closing function
  const handleCloseModal = () => {
    setModalOpen(false);
    setErrorMessage("");
  };

  /** Theme `lg` is 992px — same rail breakpoint as the drawer. */
  const isDesktopRailViewport = useCallback(
    () => typeof window !== "undefined" && window.innerWidth >= 992,
    []
  );

  // Hover-expand rail when unpinned (auto open/close)
  const handleOnMouseEnter = () => {
    if (sidenavHoverLeaveTimerRef.current) {
      clearTimeout(sidenavHoverLeaveTimerRef.current);
      sidenavHoverLeaveTimerRef.current = null;
    }
    if (sidenavPinnedRef.current || !isDesktopRailViewport()) return;
    if (miniSidenav) {
      sidenavHoverRailExpandRef.current = true;
      setMiniSidenav(dispatch, false);
    }
  };

  const handleOnMouseLeave = () => {
    if (sidenavPinnedRef.current || !isDesktopRailViewport()) return;
    if (!sidenavHoverRailExpandRef.current) return;
    if (sidenavHoverLeaveTimerRef.current) {
      clearTimeout(sidenavHoverLeaveTimerRef.current);
    }
    sidenavHoverLeaveTimerRef.current = setTimeout(() => {
      sidenavHoverLeaveTimerRef.current = null;
      if (sidenavPinnedRef.current || !isDesktopRailViewport()) return;
      if (sidenavHoverRailExpandRef.current) {
        sidenavHoverRailExpandRef.current = false;
        setMiniSidenav(dispatch, true);
      }
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (sidenavHoverLeaveTimerRef.current) {
        clearTimeout(sidenavHoverLeaveTimerRef.current);
      }
    };
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  const getRoutes = (allRoutes: any) =>
    allRoutes.map((route: any) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        if (
          !route?.whiteListed &&
          user &&
          !canAccessRouteEntry(userRoles, route, { profilesMenuVisible, user })
        ) {
          return (
            <Route
              path={route.route}
              element={<Navigate to="/dashboard" replace />}
              key={`${route.key}-unauthorized`}
            />
          );
        }

        return route?.whiteListed ? (
          <Route path={route.route} element={route.component} key={route.key} />
        ) : (
          <Route
            path={route.route}
            element={
              <ProtectedRoute user={user}>{route.component}</ProtectedRoute>
            }
            key={route.key}
          />
        );
      }

      return null;
    });

  const isPublicRoute = isPublicAppPath(pathname);
  const showSecurityOverlay = !isPublicRoute && (securityChecking || !authReady);

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <Notification ref={notificationElement} />
      <ErrorModal
        open={isModalOpen}
        handleClose={handleCloseModal}
        message={errorMessage}
      />
      <CssBaseline />
      {showSecurityOverlay && (
        <FullscreenLoadingOverlay message={t("auth.checkingSecurity")} />
      )}

      {(authReady || isPublicRoute) && !showSecurityOverlay && (
        <>
          {layout === "dashboard" && (
            <Sidenav
              color={sidenavColor}
              brand={darkMode ? logoDarkTheme : logoLightTheme}
              brandName={brandName}
              routes={routes}
              userRoles={userRoles}
              user={user}
              profilesMenuVisible={profilesMenuVisible}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
              size="large"
            />
          )}

          <DisplayLocalizationProvider key={user ? "signed-in" : "guest"}>
            <UiLanguageSync />
            <ConfirmProvider
              defaultOptions={{
                title: t("common.areYouSure"),
                confirmationText: t("common.confirm"),
                cancellationText: t("common.cancel"),
                dialogProps: {
                  maxWidth: "xs",
                  fullWidth: true,
                },
              }}
            >
              <Routes>
                {getRoutes(routes)}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
            </ConfirmProvider>
          </DisplayLocalizationProvider>

          <Configurator />
        </>
      )}
    </ThemeProvider>
  );
}
